import { createContext, useContext } from "react";
import type {
    ExpansionState,
    FlatItem,
    GroupedTableInternalContextValue,
    GroupedTableState,
    GroupId,
    GroupNode,
    GroupPath,
} from "./types";

export const ROOT_ID: GroupId = "__grouped_table_root__";

export function encodePart(x: unknown): string {
    return encodeURIComponent(String(x ?? "__NULL__"));
}

export function defaultMakeGroupId(path: GroupPath): GroupId {
    if (path.length === 0) {
    return ROOT_ID;
    }

    return path
        .map(p => `${encodePart(p.fieldId)}=${encodePart(p.value)}`)
        .join("|");
}

export function isGroupExpanded<TRow>(
    group: GroupNode<TRow>,
    exp: ExpansionState,
    rootId: GroupId,
): boolean {
    if (group.id === rootId) {
        return true;
    }

    if (exp.collapsed[group.id]) {
        return false;
    }

    if (exp.expanded[group.id]) {
        return true;
    }

    return group.depth < exp.globalExpandedDepth;
}

export function toMap(ids: GroupId[] | undefined): Record<GroupId, boolean> {
    const out: Record<GroupId, boolean> = {};

    for (const id of ids ?? []) {
        out[id] = true;
    }

    return out;
}

export function buildFlatItems<TRow>(
    state: GroupedTableState<TRow>,
): FlatItem[] {
    const items: FlatItem[] = [];

    const { groups, expansion, rootId } = state;
    const root = groups[rootId];

    if (!root) {
        return items;
    }

    if (root.status === "not-loaded" || root.status === "loading") {
        items.push({ kind: "loading", parentGroupId: rootId, key: "root-loading" });
        return items;
    }

    if (root.status === "error") {
        items.push({
            kind: "error",
            parentGroupId: rootId,
            message: root.error || "Unknown error",
            key: "root-error",
        });
        return items;
    }

    for (const rowId of root.rowIds) {
        items.push({
            kind: "row",
            id: rowId,
            parentGroupId: rootId,
            key: defaultMakeGroupId(root.path) + `|row=${encodePart(rowId)}`,
        });
    }

    for (const childGroupId of root.childGroupIds) {
        walkGroup(childGroupId);
    }

    function walkGroup(groupId: GroupId) {
        const group = groups[groupId];
        if (!group) {
            return;
        }

        const groupKey = defaultMakeGroupId(group.path).toString();

        items.push({
            kind: "group",
            id: group.id,
            key: groupKey,
        });

        if (!isGroupExpanded(group, expansion, rootId)) {
            return;
        }

        if (group.status === "loading" || group.status === "not-loaded") {
            items.push({ kind: "loading", parentGroupId: group.id, key: groupKey + "|loading" });
            return;
        }

        if (group.status === "error") {
            items.push({
                kind: "error",
                parentGroupId: group.id,
                message: group.error || "Unknown error",
                key: groupKey + "|error",
            });
            return;
        }

        for (const childId of group.childGroupIds) {
            walkGroup(childId);
        }

        for (const rowId of group.rowIds) {
            items.push({
                kind: "row",
                id: rowId,
                parentGroupId: group.id,
                key: groupKey + `|row=${encodePart(rowId)}`,
            });
        }
    }

    return items;
}

export const GroupedTableInternalContext = createContext<unknown>(null);
export function useGroupedTableInternal<TRow>(): GroupedTableInternalContextValue<TRow> {
    const ctx = useContext(GroupedTableInternalContext);
    if (!ctx) {
        throw new Error("useGroupedTableInternal must be used within a GroupedTableProvider");
    }

    return ctx as GroupedTableInternalContextValue<TRow>;
}